import { supabase } from '../supabase';
import type { MarketListing, MarketFilters, Game, UserProfile } from '../../types';
import { processAction } from '../gamification/gamificationService';
import { XP_ACTIONS } from '../gamification/xpConfig';
import { UserProfile as AppUserProfile } from '../../types';

/**
 * Serviço para gerenciar funcionalidades de mercado (compra e venda de jogos)
 */
class MarketService {
  /**
   * Cria um novo anúncio no mercado
   * 
   * @param userId ID do usuário criando o anúncio
   * @param gameId ID do jogo
   * @param listingType Tipo de anúncio (venda, compra ou troca)
   * @param price Preço em reais (opcional)
   * @param condition Condição do jogo (opcional)
   * @param description Descrição adicional (opcional)
   * @param location Localização para retirada/entrega (opcional)
   * @param city Cidade (opcional)
   * @param state Estado (opcional)
   * @param country País (opcional)
   * @returns Resultado da operação
   */
  async createListing(
    userId: string,
    gameId: string,
    listingType: 'sell' | 'buy' | 'trade',
    price: number | null,
    condition?: 'new' | 'like_new' | 'good' | 'fair' | 'poor' | null,
    description?: string | null,
    location?: string | null,
    city?: string | null,
    state?: string | null,
    country?: string | null
  ): Promise<{
    success: boolean;
    data?: MarketListing;
    error?: string;
  }> {
    try {
      if (!userId) {
        return { success: false, error: 'Usuário não autenticado' };
      }

      if (listingType === 'sell') {
        const { data: userGame, error: userGameError } = await supabase
          .from('user_games')
          .select('id')
          .eq('user_id', userId)
          .eq('game_id', gameId)
          .eq('owned', true)
          .maybeSingle();

        if (userGameError) {
          console.error('Erro ao verificar posse do jogo:', userGameError);
          return { success: false, error: 'Erro ao verificar sua coleção de jogos.' };
        }
        if (!userGame) {
          return {
            success: false,
            error: 'Você precisa possuir o jogo em sua coleção para anunciá-lo para venda.',
          };
        }
      }

      const listingPayload: any = {
        user_id: userId,
        game_id: gameId,
        listing_type: listingType,
        price: price,
        condition: condition,
        description: description,
        location: location,
        city: city,
        state: state,
        country: country,
        status: 'active' as const,
      };
      
      const { data: insertedData, error: insertError } = await supabase
        .from('market_listings')
        .insert(listingPayload)
        .select('id')
        .single();

      if (insertError) {
        console.error('Erro ao criar anúncio (insert):', insertError);
        return { success: false, error: insertError.message || 'Não foi possível criar o anúncio' };
      }

      if (!insertedData || !insertedData.id) {
        console.error('Falha ao obter ID do anúncio inserido.');
        return { success: false, error: 'Falha ao criar o anúncio, ID não retornado.' };
      }

      const newListingDetails = await this.getListingDetails(insertedData.id);

      if (!newListingDetails) {
        console.error(`Falha ao buscar detalhes do anúncio recém-criado: ${insertedData.id}`);
        return { success: false, error: 'Anúncio criado, mas falha ao buscar detalhes completos.' };
      }

      return { success: true, data: newListingDetails };
    } catch (error: any) {
      console.error('Exceção em createListing:', error);
      return { success: false, error: error.message || 'Erro desconhecido ao criar anúncio' };
    }
  }

  /**
   * Atualiza um anúncio existente
   * 
   * @param listingId ID do anúncio
   * @param userId ID do usuário fazendo a atualização (para verificação)
   * @param updateData Campos a serem atualizados
   * @returns Resultado da operação
   */
  async updateListing(
    listingId: string,
    userId: string,
    updateData: Partial<Omit<MarketListing, 'id' | 'user_id' | 'game_id' | 'created_at' | 'updated_at' | 'user_profile' | 'game' | 'latitude' | 'longitude'>>
  ): Promise<{
    success: boolean;
    data?: MarketListing;
    error?: string;
  }> {
    try {
      const { data: existingListing, error: fetchError } = await supabase
        .from('market_listings')
        .select('user_id')
        .eq('id', listingId)
        .single();

      if (fetchError) {
        console.error(`Erro ao buscar anúncio ${listingId} para atualização:`, fetchError.message);
        return { success: false, error: 'Anúncio não encontrado ou erro ao buscar.' };
      }

      if (!existingListing) {
        return { success: false, error: 'Anúncio não encontrado.' };
      }

      if (existingListing.user_id !== userId) {
        return { success: false, error: 'Você não tem permissão para editar este anúncio.' };
      }

      const payloadForSupabase: { [key: string]: any } = {};

      for (const key in updateData) {
        if (Object.prototype.hasOwnProperty.call(updateData, key)) {
          const value = updateData[key as keyof typeof updateData];

          if (key === 'price') {
            if (value === undefined || value === null || String(value).trim() === '') {
              payloadForSupabase[key] = null;
            } else {
              const numValue = Number(value);
              payloadForSupabase[key] = isNaN(numValue) ? null : numValue;
            }
          } else if (value !== undefined) {
            payloadForSupabase[key] = value;
          }
        }
      }
      
      if (Object.keys(payloadForSupabase).length === 0) {
        const currentDetails = await this.getListingDetails(listingId);
        if (!currentDetails) return { success: false, error: 'Anúncio não encontrado após tentativa de atualização vazia.' };
        return { success: true, data: currentDetails, error: 'Nenhum dado fornecido para atualização.' }; 
      }

      const { data: updatedResult, error: updateError } = await supabase
        .from('market_listings')
        .update({
          ...payloadForSupabase,
          updated_at: new Date().toISOString(),
        })
        .eq('id', listingId)
        .select('id') 
        .single();

      if (updateError) {
        console.error(`Erro ao atualizar anúncio ${listingId}:`, updateError.message);
        return { success: false, error: updateError.message || 'Não foi possível atualizar o anúncio' };
      }
      
      if (!updatedResult || !updatedResult.id) {
        console.error('Falha ao obter ID do anúncio atualizado.');
        return { success: false, error: 'Falha ao atualizar o anúncio, ID não retornado.' };
      }

      const updatedListingDetails = await this.getListingDetails(updatedResult.id);

      if (!updatedListingDetails) {
        console.error(`Falha ao buscar detalhes do anúncio recém-atualizado: ${updatedResult.id}`);
        return { success: false, error: 'Anúncio atualizado, mas falha ao buscar detalhes completos.' };
      }

      return { success: true, data: updatedListingDetails };
    } catch (error: any) {
      console.error(`Exceção em updateListing para ${listingId}:`, error);
      return { success: false, error: error.message || 'Erro desconhecido ao atualizar anúncio' };
    }
  }

  /**
   * Marca um anúncio como concluído (vendido/comprado)
   * 
   * @param listingId ID do anúncio
   * @param userId ID do usuário fazendo a marcação (para verificação)
   * @returns Resultado da operação
   */
  async markListingAsCompleted(
    listingId: string,
    userId: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    const updateResult = await this.updateListing(listingId, userId, { status: 'completed' });

    if (updateResult.success && updateResult.data) {
        // Chamar gamificação
        processAction({
            supabase: supabase,
            userId: userId, // Quem marcou como concluído (vendedor ou comprador, dependendo da lógica de negócio)
            actionKey: XP_ACTIONS.COMPLETE_MARKET_LISTING,
            sourceId: updateResult.data.id,
            details: { 
                gameId: updateResult.data.game_id, 
                original_poster_id: updateResult.data.user_id // ID de quem criou o anúncio
            }
        }).catch((err: any) => console.warn(`[Gamification Warning] Failed processAction for COMPLETE_MARKET_LISTING ${updateResult.data?.id}:`, err));
    }
    return { success: true };
  }

  /**
   * Cancela um anúncio
   * 
   * @param listingId ID do anúncio
   * @param userId ID do usuário fazendo o cancelamento (para verificação)
   * @returns Resultado da operação
   */
  async cancelListing(
    listingId: string,
    userId: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    return this.updateListing(listingId, userId, { status: 'canceled' });
  }

  /**
   * Deleta um anúncio
   * 
   * @param listingId ID do anúncio
   * @param userId ID do usuário fazendo a deleção (para verificação)
   * @param isAdmin Se o usuário é um administrador
   * @returns Resultado da operação
   */
  async deleteListing(
    listingId: string,
    userId: string,
    isAdmin: boolean = false
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      console.log('Iniciando exclusão do anúncio:', listingId);
      console.log('Usuário ID:', userId);
      console.log('É administrador:', isAdmin);
      
      // Verificar se o anúncio existe e pertence ao usuário (a menos que seja admin)
      const { data: existingListing, error: fetchError } = await supabase
        .from('market_listings')
        .select('*')
        .eq('id', listingId)
        .single();

      if (fetchError) {
        console.error('Erro ao buscar anúncio para exclusão:', fetchError);
        return {
          success: false,
          error: 'Anúncio não encontrado'
        };
      }

      if (!existingListing) {
        console.error('Anúncio não encontrado para exclusão:', listingId);
        return {
          success: false,
          error: 'Anúncio não encontrado'
        };
      }

      console.log('Anúncio encontrado:', existingListing);

      // Se não for admin, verificar se o usuário é o proprietário do anúncio
      if (!isAdmin && existingListing.user_id !== userId) {
        console.error('Usuário não tem permissão para excluir este anúncio');
        return {
          success: false,
          error: 'Você não tem permissão para excluir este anúncio'
        };
      }

      // Abordagem alternativa: como pode haver restrições de deleção,
      // vamos tentar marcar o anúncio como "excluído" atualizando seu status
      console.log('Tentando usar abordagem alternativa: marcar como excluído...');
      const { error: updateError } = await supabase
        .from('market_listings')
        .update({
          status: 'deleted',
          updated_at: new Date().toISOString()
        })
        .eq('id', listingId);

      if (updateError) {
        console.error('Erro ao atualizar status para excluído:', updateError);
        
        // Se a atualização falhar, tentar a exclusão direta como último recurso
        console.log('Executando exclusão direta do anúncio no banco de dados...');
        
        const { error } = await supabase
          .from('market_listings')
          .delete()
          .eq('id', listingId);
  
        if (error) {
          console.error('Erro ao excluir anúncio:', error);
          return {
            success: false,
            error: 'Não foi possível excluir o anúncio'
          };
        }
      } else {
        console.log('Anúncio marcado como excluído com sucesso!');
      }
      
      // Verificar se o anúncio ainda está ativo
      const { data: stillActive, error: checkError } = await supabase
        .from('market_listings')
        .select('status')
        .eq('id', listingId)
        .eq('status', 'active')
        .single();
      
      if (!checkError && stillActive) {
        console.error('Anúncio ainda está ativo após tentativa de exclusão');
        return {
          success: false,
          error: 'Não foi possível remover o anúncio do mercado ativo'
        };
      }
      
      console.log('Anúncio removido com sucesso do mercado ativo:', listingId);
      return { success: true };
    } catch (error: any) {
      console.error('Erro ao excluir anúncio:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido'
      };
    }
  }

  /**
   * Busca anúncios com filtros
   * 
   * @param filters Filtros para busca
   * @param limit Limite de resultados por página
   * @param offset Offset para paginação
   * @returns Lista de anúncios com detalhes de jogos e usuários
   */
  async getListings(
    filters?: MarketFilters,
    limit: number = 20,
    offset: number = 0
  ): Promise<{
    data: MarketListing[];
    count: number;
    error?: string;
  }> {
    try {
      let query = supabase
        .from('market_listings')
        .select(
          `
          id,
          user_id,
          game_id,
          listing_type,
          price,
          condition,
          description,
          location, 
          status,
          created_at,
          updated_at,
          game:games(id, name, image_url, year, thumb_url, slug)
        `,
          { count: 'exact' }
        );

      // Aplicar filtros
      if (filters?.status) {
        query = query.eq('status', filters.status);
      } else {
        query = query.in('status', ['active', 'pending']);
      }

      if (filters?.exclude_user_id) {
        query = query.not('user_id', 'eq', filters.exclude_user_id);
      }

      if (filters?.listing_type) {
        query = query.eq('listing_type', filters.listing_type);
      }

      if (filters?.min_price !== undefined && filters.min_price !== null) {
        query = query.gte('price', filters.min_price);
      }

      if (filters?.max_price !== undefined && filters.max_price !== null) {
        query = query.lte('price', filters.max_price);
      }
      
      if (filters?.condition && filters.condition.length > 0) {
        query = query.in('condition', filters.condition);
      }
      
      // Filtro para o campo location geral
      if (filters?.location) { // Supondo que o filtro venha como filters.location
        query = query.ilike('location', `%${filters.location}%`);
      }
      
      // TODO: Adicionar filtros para `categories`, `mechanics`, `min_players`, `max_players` (da tabela games)

      if (filters?.sort_by) {
        const directSortableFields = ['created_at', 'price', 'updated_at'];
        if (directSortableFields.includes(filters.sort_by)) {
          query = query.order(filters.sort_by as 'created_at' | 'price' | 'updated_at', {
            ascending: filters.order === 'asc',
          });
        }
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(offset, offset + limit - 1);
      
      const { data: listingsData, error: listingsError, count } = await query;

      if (listingsError) {
        console.error('Error fetching listings (initial query with filters):', listingsError);
        return { data: [], count: 0, error: listingsError.message };
      }

      if (!listingsData || listingsData.length === 0) {
        return { data: [], count: count || 0 };
      }

      const userIds = listingsData.map((listing) => listing.user_id).filter(id => !!id) as string[];
      let profilesMap = new Map<string, UserProfile>();

      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, full_name')
          .in('id', userIds);

        if (profilesError) {
          console.warn('Error fetching user profiles for listings:', profilesError.message);
        } else if (profilesData) {
          profilesMap = new Map(profilesData.map((profile) => [profile.id, profile as UserProfile]));
        }
      }
      
      let enrichedData = listingsData.map((listing) => {
        const gameData = listing.game as Game | null;
        return {
          ...listing,
          game: gameData,
          user_profile: profilesMap.get(listing.user_id),
        } as MarketListing;
      });

      if (filters?.search_text) {
        const searchText = filters.search_text.toLowerCase();
        enrichedData = enrichedData.filter(
          (listing) =>
            (listing.game?.name?.toLowerCase().includes(searchText)) ||
            (listing.description?.toLowerCase().includes(searchText)) ||
            (listing.user_profile?.username?.toLowerCase().includes(searchText)) ||
            (listing.location?.toLowerCase().includes(searchText)) // Adicionado location à busca textual
        );
      }
      
      return { data: enrichedData, count: count || 0 };
    } catch (err: any) {
      console.error('Exception in getListings:', err);
      return { data: [], count: 0, error: err.message || 'Failed to fetch listings due to an exception' };
    }
  }

  /**
   * Busca anúncios de um usuário específico
   * 
   * @param userId ID do usuário
   * @param filters Filtros adicionais para busca
   * @param limit Limite de resultados por página
   * @param offset Offset para paginação
   * @returns Lista de anúncios do usuário
   */
  async getUserListings(
    userId: string,
    filters?: Omit<MarketFilters, 'user_id'> & { include_sold?: boolean }, 
    limit: number = 10,
    offset: number = 0
  ): Promise<{
    data: MarketListing[];
    count: number;
    error?: string;
  }> {
    try {
      let query = supabase
        .from('market_listings')
        .select(
          `
          *,
          game:games(*)
        `,
          { count: 'exact' }
        )
        .eq('user_id', userId);

      // Aplicar filtros de status, se fornecidos (excluindo 'user_id' que já está aplicado)
      if (filters?.status) {
        query = query.eq('status', filters.status);
      } else {
        // Nova lógica de status padrão para getUserListings
        if (filters?.include_sold === false) { // Se explicitamente false, mostrar só ativos
          query = query.eq('status', 'active');
        } else { // Se true ou undefined (padrão ao carregar a aba), mostrar ativos, concluídos e pendentes
          query = query.in('status', ['active', 'completed', 'pending']);
        }
      }

      if (filters?.sort_by) {
        const [sortField, sortOrder] = filters.sort_by.split('.');
        if (['created_at', 'price', 'updated_at'].includes(sortField)) {
          query = query.order(sortField as 'created_at' | 'price' | 'updated_at', {
            ascending: sortOrder === 'asc',
          });
        }
      } else {
        query = query.order('created_at', { ascending: false });
      }

      query = query.range(offset, offset + limit - 1);

      const { data: listingsData, error: listingsError, count } = await query;

      if (listingsError) {
        console.error('Error fetching user listings (initial query):', listingsError);
        return { data: [], count: 0, error: listingsError.message };
      }

      if (!listingsData || listingsData.length === 0) {
        return { data: [], count: count || 0 };
      }

      // O user_id de todos os listingsData é o mesmo (userId do parâmetro).
      // Então, buscamos o perfil desse usuário uma única vez.
      let userProfile: UserProfile | undefined = undefined;
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, avatar_url, full_name')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.warn(`Error fetching profile for user ${userId}:`, profileError.message);
      } else if (profileData) {
        userProfile = profileData as UserProfile;
      }

      let enrichedData = listingsData.map((listing) => {
        const gameData = listing.game as Game | null;
        return {
          ...listing,
          game: gameData,
          user_profile: userProfile, // Todos os anúncios aqui são do mesmo usuário
        } as MarketListing;
      });
      
      // Aplicar filtros que dependem de dados embutidos (como nome do jogo)
      if (filters?.search_text) {
        const searchText = filters.search_text.toLowerCase();
        enrichedData = enrichedData.filter(
          (listing) =>
            listing.game?.name?.toLowerCase().includes(searchText) ||
            listing.description?.toLowerCase().includes(searchText)
            // Não faz sentido filtrar por user_profile.username aqui, pois é sempre o mesmo usuário
        );
      }

      return { data: enrichedData, count: count || 0 };
    } catch (err: any) {
      console.error('Exception in getUserListings:', err);
      const message = err instanceof Error ? err.message : 'Erro desconhecido ao buscar anúncios do usuário';
      return { data: [], count: 0, error: message };
    }
  }

  /**
   * Obtém detalhes de um anúncio específico
   * 
   * @param listingId ID do anúncio
   * @returns Detalhes do anúncio ou null se não encontrado
   */
  async getListingDetails(listingId: string): Promise<MarketListing | null> {
    try {
      if (!listingId) {
        console.warn('getListingDetails called with no listingId');
        return null;
      }

      const { data: listingData, error: listingError } = await supabase
        .from('market_listings')
        .select(
          `
          *,
          game:games(*)
        `
        )
        .eq('id', listingId)
        .not('status', 'eq', 'deleted') // Não mostrar anúncios marcados como deletados
        .single();

      if (listingError) {
        console.error(`Error fetching listing details for ${listingId}:`, listingError.message);
        if (listingError.code === 'PGRST116') {
            return null; 
        }
        throw listingError; 
      }

      if (!listingData) {
        return null;
      }

      let userProfile: UserProfile | undefined = undefined;
      if (listingData.user_id) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          // Remover city e state pois não existem na tabela profiles conforme db.schema
          .select('id, username, avatar_url, full_name, bio, created_at') 
          .eq('id', listingData.user_id)
          .single();

        if (profileError) {
          console.warn(
            `Error fetching profile for user ${listingData.user_id} on listing ${listingId}:`,
            profileError.message
          );
        } else if (profileData) {
          userProfile = profileData as UserProfile;
        }
      }
      
      const gameData = listingData.game as Game | null;

      return {
        ...(listingData as Omit<MarketListing, 'game' | 'user_profile'>),
        game: gameData,
        user_profile: userProfile,
      } as MarketListing;
    } catch (error: any) {
      console.error(`Generic error in getListingDetails for ${listingId}:`, error.message);
      return null;
    }
  }

  /**
   * Busca anúncios de um jogo específico
   * 
   * @param gameId ID do jogo
   * @param listingType Tipo de anúncio (opcional)
   * @returns Lista de anúncios com detalhes de usuários
   */
  async getListingsByGame(
    gameId: string,
    listingType?: 'sell' | 'buy'
  ): Promise<MarketListing[]> {
    try {
      let query = supabase
        .from('market_listings')
        .select(
          `*, games!game_id!inner(id, name, image_url, slug), profiles!user_id!inner(id, username, avatar_url, full_name)`,
          { count: 'exact' }
        )
        .eq('game_id', gameId)
        .eq('status', 'active');

      if (listingType) {
        query = query.eq('listing_type', listingType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar anúncios para o jogo:', error);
        return [];
      }

      return data as MarketListing[];
    } catch (error) {
      console.error('Erro ao buscar anúncios para o jogo:', error);
      return [];
    }
  }

  /**
   * Verifica se um anúncio específico existe no banco de dados
   * 
   * @param listingId ID do anúncio a verificar
   * @returns true se o anúncio existe, false caso contrário
   */
  async checkListingExists(listingId: string): Promise<boolean> {
    try {
      console.log('Verificando se o anúncio existe:', listingId);
      
      const { data, count, error } = await supabase
        .from('market_listings')
        .select('id', { count: 'exact' })
        .eq('id', listingId)
        .limit(1);
      
      if (error) {
        console.error('Erro ao verificar existência do anúncio:', error);
        return false;
      }
      
      const exists = (count ?? 0) > 0;
      console.log('Resultado da verificação:', exists ? 'Anúncio existe' : 'Anúncio não existe');
      
      return exists;
    } catch (error) {
      console.error('Erro ao verificar existência do anúncio:', error);
      return false;
    }
  }
}

export const marketService = new MarketService(); 